import { ROUTES, IRouteItem } from './routes';
import MENU_ITEMS, { IMenuItem } from './menu-items';
import { localAuthEmulation } from 'utils/localAuthEmulationGate';

export interface IPermission {
  route: IRouteItem;
  menu?: IMenuItem;
}

export type Role = 'admin' | 'logistician' | 'producer' | 'superuser' | 'quality' | 'store' | 'adminlogistician';

export type IPermissions = {
  [key in Role]?: IPermission[];
};

const PERMISSIONS: IPermissions = {
  admin: [
    { route: ROUTES.homeRedirect },
    { route: ROUTES.dashboard, menu: MENU_ITEMS.dashboard },
    { route: ROUTES.productAssistant, menu: MENU_ITEMS.productAssistant },
    { route: ROUTES.producers, menu: MENU_ITEMS.producers },
    { route: ROUTES.products, menu: MENU_ITEMS.products },
    { route: ROUTES.addProducer },
    { route: ROUTES.addProduct },
    { route: ROUTES.showProduct },
    { route: ROUTES.showProducer },
    { route: ROUTES.producerProducts },
    { route: ROUTES.producerShowProduct },
  ],
  logistician: [
    { route: ROUTES.dashboard, menu: MENU_ITEMS.dashboard },
    { route: ROUTES.producers, menu: MENU_ITEMS.producers },
    { route: ROUTES.products, menu: MENU_ITEMS.products },
    { route: ROUTES.orderStep },
    { route: ROUTES.orderNotification },
    { route: ROUTES.showProduct },
    { route: ROUTES.showProducer },
    { route: ROUTES.producerProducts },
    { route: ROUTES.producerShowProduct },
  ],
  producer: [
    { route: ROUTES.homeRedirect },
    { route: ROUTES.dashboard, menu: MENU_ITEMS.dashboard },
    { route: ROUTES.productAssistant, menu: MENU_ITEMS.productAssistant },
    { route: ROUTES.insights, menu: MENU_ITEMS.insights },
    { route: ROUTES.me, menu: MENU_ITEMS.me },
    { route: ROUTES.meProducts, menu: MENU_ITEMS.meProducts },
    { route: ROUTES.meShowProduct },
    { route: ROUTES.orderNotification },
    { route: ROUTES.addProduct },
    { route: ROUTES.meEditProduct },
    { route: ROUTES.meEdit },
    { route: ROUTES.producerData },
    // { route: ROUTES.meBulkImport, menu: MENU_ITEMS.meBulkImport },
    { route: ROUTES.meBulkImportNew, menu: MENU_ITEMS.meBulkImport },
    { route: ROUTES.producerIcaAssortmentsProducts },
    { route: ROUTES.producerIcaAssortmentsProductsOnline },
  ],
  superuser: [
    { route: ROUTES.homeRedirect },
    { route: ROUTES.dashboard, menu: MENU_ITEMS.dashboard },
    // Superusers choose the account to work with on the assistant page; it opens from the Product Assistant
    // settings and statistics, not from the menu. With local auth emulation (`yarn noauth`) the menu item is
    // shown so the emulated superuser can open the assistant directly.
    { route: ROUTES.productAssistant, ...(process.env.NODE_ENV === 'development' && localAuthEmulation ? { menu: MENU_ITEMS.productAssistant } : {}) },
    { route: ROUTES.superuserData },
    { route: ROUTES.superuserProducts, menu: MENU_ITEMS.superuserProducts },
    { route: ROUTES.superuserBulkReview, menu: MENU_ITEMS.superuserBulkReview },
    { route: ROUTES.superuserBulkReviewQualityGroups },
    { route: ROUTES.superuserBulkReviewQualityGroupReview },
    // { route: ROUTES.superuserTmp, menu: MENU_ITEMS.superuserTmp },
    { route: ROUTES.superuserProducers, menu: MENU_ITEMS.superuserProducers },
    { route: ROUTES.superuserOrganizations, menu: MENU_ITEMS.superuserAllOrganizations },
    { route: ROUTES.superuserAllProductsList, menu: MENU_ITEMS.superuserAllProducts },
    { route: ROUTES.superuserAllProducersList, menu: MENU_ITEMS.superuserAllProducers },
    { route: ROUTES.superuserApprovalForm },
    { route: ROUTES.superuserCreateOrganization },
    { route: ROUTES.superuserEditOrganization },
    { route: ROUTES.superuserEditProducer },
    { route: ROUTES.superuserAddProducer },
    { route: ROUTES.producerProducts },
    { route: ROUTES.superuserFailedProducts, menu: MENU_ITEMS.failedProducts },
    { route: ROUTES.superuserExportProducts },
    { route: ROUTES.superuserWoltExportProducts },
    { route: ROUTES.superuserIcaExportProducts },
    { route: ROUTES.superuserApoteaExportProducts },
    // { route: ROUTES.superuserExportProducts, menu: MENU_ITEMS.superuserExportProducts },
    { route: ROUTES.superuserExportNonFoodProducts, menu: MENU_ITEMS.superuserExportNonFoodProducts },
    { route: ROUTES.superuserErrors, menu: MENU_ITEMS.superuserErrors },
    { route: ROUTES.exportHistory, menu: MENU_ITEMS.exportHistory },
    { route: ROUTES.icaExportHistory, menu: MENU_ITEMS.icaExportHistory },
    { route: ROUTES.bulkImport, menu: MENU_ITEMS.bulkImport },
    { route: ROUTES.bulkImportNew, menu: MENU_ITEMS.bulkImportNew },
    { route: ROUTES.superuserWatermark },
  ],
  quality: [
    { route: ROUTES.homeRedirect },
    { route: ROUTES.dashboard, menu: MENU_ITEMS.dashboard },
    { route: ROUTES.superuserProducts, menu: MENU_ITEMS.superuserProducts },
    //   { route: ROUTES.superuserTmp, menu: MENU_ITEMS.superuserTmp },
    { route: ROUTES.superuserApprovalForm },
    { route: ROUTES.superuserFailedProducts, menu: MENU_ITEMS.failedProducts },
    //   { route: ROUTES.superuserExportProducts, menu: MENU_ITEMS.superuserExportProducts },
  ],
  store: [
    { route: ROUTES.homeRedirect },
    { route: ROUTES.dashboard, menu: MENU_ITEMS.dashboard },
    { route: ROUTES.productAssistant, menu: MENU_ITEMS.productAssistant },

    { route: ROUTES.me, menu: MENU_ITEMS.me },
    { route: ROUTES.meEdit },

    { route: ROUTES.showProduct },
    { route: ROUTES.storeEditProduct },
    { route: ROUTES.storeShowProduct },
    { route: ROUTES.producerShowProduct },
    { route: ROUTES.storeAddProduct },

    { route: ROUTES.storeProducts, menu: MENU_ITEMS.storeProducts },
    { route: ROUTES.producers, menu: MENU_ITEMS.producers },
    { route: ROUTES.storeAllProducts, menu: MENU_ITEMS.storeAllProducts },
    // { route: ROUTES.addProducer },
    { route: ROUTES.showProducer },
    { route: ROUTES.producerProducts },
    // { route: ROUTES.storeBulkImport, menu: MENU_ITEMS.storeBulkImport },
  ],
};

// For when user is both admin and logistician
PERMISSIONS.adminlogistician = PERMISSIONS.logistician;

// The Settings group stays at the end of the menu.
PERMISSIONS.superuser?.push(
  { route: ROUTES.superuserSettingsAi, menu: MENU_ITEMS.superuserSettingsAi },
  { route: ROUTES.superuserStatistics, menu: MENU_ITEMS.superuserStatistics },
);

if (process.env.REACT_APP_AUTH_ENV === "local") {
  PERMISSIONS.superuser?.push({
    route: ROUTES.superuserDatabase,
    menu: MENU_ITEMS.superuserDatabase,
  });
}

export default PERMISSIONS;

// Insights is shown to producers whose `enableInsights` a superuser turned on.
export const getRolePermissions = (role: Role, enableProductAssistant = false, enableInsights = false): IPermission[] =>
  (PERMISSIONS[role] || []).filter(permission =>
    (permission.route !== ROUTES.productAssistant || enableProductAssistant === true || role === 'superuser') &&
    (permission.route !== ROUTES.insights || enableInsights === true)
  );
